import { useCallback, useEffect, useState } from 'react'
import { theme } from '../theme'
import { Ic, RecipeThumb } from '../icons'
import { DOW_LONG, SLOT_LABELS } from '../dates'
import { ModalShell } from './ModalShell'
import { api } from '../api'
import { Highlight } from '../search'
import { ComboboxList, useCombobox, type ComboGroup } from './Combobox'
import type { Recipe, RecipeSearchRow, SlotKey, LeftoverLot } from '../types'

export function RecipePickerModal({
  open, onClose, dayIso, slotKey, currentRecipeId, onPick, onPickLeftover, onClear, onPreview,
}: {
  open: boolean
  onClose: () => void
  dayIso: string | null
  slotKey: SlotKey | null
  currentRecipeId?: number
  onPick: (recipe: Recipe) => void
  onPickLeftover?: (pantryItemId: number) => void
  onClear: () => void
  onPreview?: (recipe: Recipe) => void
}) {
  const [q, setQ] = useState('')
  useEffect(() => { if (open) setQ('') }, [open, dayIso, slotKey])

  // Meal-eligible recipe lots on hand — offered atop the picker as zero-effort
  // "eat the leftovers" choices. Loaded each time the picker opens.
  const [leftovers, setLeftovers] = useState<LeftoverLot[]>([])
  useEffect(() => {
    if (!open) return
    let alive = true
    api.leftovers().then(ls => { if (alive) setLeftovers(ls) }).catch(() => {})
    return () => { alive = false }
  }, [open, dayIso, slotKey])
  const visibleLeftovers = q
    ? leftovers.filter(l => l.name.toLowerCase().includes(q.toLowerCase()))
    : leftovers

  // Retain the last non-null slot/day during the close transition so the
  // panel keeps rendering content while it fades out.
  const [cached, setCached] = useState<{ dayIso: string; slotKey: SlotKey } | null>(null)
  useEffect(() => {
    if (dayIso && slotKey) setCached({ dayIso, slotKey })
  }, [dayIso, slotKey])
  const liveDayIso = dayIso ?? cached?.dayIso ?? null
  const liveSlotKey = slotKey ?? cached?.slotKey ?? null

  const fetcher = useCallback(async (query: string): Promise<ComboGroup<RecipeSearchRow>[]> => {
    const res = await api.recipesSearch(query)
    return [
      { label: 'My cookbook', items: res.owned, owned: true },
      { label: 'Catalog', items: res.catalog },
    ]
  }, [])

  const handlePick = useCallback(async (row: RecipeSearchRow) => {
    if (row.owned) {
      onPick(row)
      return
    }
    // Catalog pick: fork into the user's cookbook first, then schedule the
    // forked copy. Backend adopt is idempotent and returns the existing forked
    // id when called twice.
    try {
      const { id } = await api.adopt(row.id)
      const { owned: _owned, author_name: _author, ...base } = row
      void _owned; void _author
      onPick({ ...base, id })
    } catch (e) {
      console.error(e)
    }
  }, [onPick])

  const combo = useCombobox<RecipeSearchRow>({
    query: q, fetcher, active: open,
    onPick: handlePick,
  })

  const dateObj = liveDayIso ? new Date(liveDayIso + 'T00:00:00') : null
  const dateLabel = dateObj
    ? `${DOW_LONG[(dateObj.getDay() + 6) % 7]}, ${dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
    : ''

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      zIndex={50}
      panelStyle={{
        width: 'min(560px, 100%)', maxHeight: '82%', overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <div style={{ padding: '16px 18px 10px', borderBottom: `1px solid ${theme.line}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{
                fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
                textTransform: 'uppercase', color: theme.muted,
              }}>{currentRecipeId ? 'Change' : 'Add to plan'}</div>
              <div style={{
                fontFamily: theme.serif, fontSize: 24, fontWeight: theme.headWeight,
                color: theme.ink, marginTop: 2,
              }}>
                {liveSlotKey && SLOT_LABELS[liveSlotKey]} <span style={{ color: theme.muted, fontStyle: 'italic' }}>·</span> {dateLabel}
              </div>
            </div>
            <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', color: theme.inkSoft, padding: 4 }}>
              <Ic.close width="18" height="18"/>
            </button>
          </div>
          <div style={{
            marginTop: 14,
            display: 'flex', alignItems: 'center', gap: 10,
            border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
            padding: '8px 12px', background: theme.surface2,
          }}>
            <Ic.search width="16" height="16" style={{ color: theme.muted }}/>
            <input
              autoFocus value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') { onClose(); return }
                combo.onKeyDown(e)
              }}
              placeholder="Search recipes…"
              style={{ all: 'unset', flex: 1, fontFamily: theme.sans, fontSize: 14, color: theme.ink }}
            />
            {q && <button onClick={() => setQ('')} style={{ all: 'unset', cursor: 'pointer', color: theme.muted }}>
              <Ic.close width="14" height="14"/>
            </button>}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 18px 18px' }}>
          {onPickLeftover && visibleLeftovers.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{
                fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.2, fontWeight: 700,
                textTransform: 'uppercase', color: theme.accent, padding: '4px 10px',
              }}>Leftovers on hand</div>
              {visibleLeftovers.map(l => (
                <button
                  key={l.pantry_item_id}
                  onClick={() => onPickLeftover(l.pantry_item_id)}
                  style={{
                    all: 'unset', cursor: 'pointer', width: '100%', boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 10px', borderRadius: theme.radius,
                  }}
                >
                  <RecipeThumb hue={30} name={l.name} size={36} radius={theme.radius}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink, fontWeight: 500 }}>{l.name}</div>
                    <div style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, marginTop: 2 }}>
                      {l.servings} serving{l.servings === 1 ? '' : 's'} on hand
                      {l.expires_at && <> · exp {l.expires_at}</>}
                    </div>
                  </div>
                  <Ic.plus width="16" height="16" style={{ color: theme.accent }}/>
                </button>
              ))}
              <div style={{ height: 1, background: theme.line, margin: '8px 10px 0' }}/>
            </div>
          )}
          <ComboboxList<RecipeSearchRow>
            api={combo}
            query={q}
            itemKey={r => r.id}
            groupHeaders
            emptyHint={q ? `No matches for "${q}".` : 'Search your cookbook or the catalog…'}
            renderItem={(r, terms) => (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 10px', borderRadius: theme.radius,
              }}>
                <RecipeThumb imageUrl={r.image_url} hue={r.hue} name={r.name} size={36} radius={theme.radius}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: theme.sans, fontSize: 14, color: theme.ink, fontWeight: 500 }}>
                    <Highlight text={r.name} terms={terms}/>
                  </div>
                  <div style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, marginTop: 2, display: 'flex', gap: 8 }}>
                    <span>{r.prep_minutes} min</span>
                    {!r.owned && r.author_name && <>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>by {r.author_name}</span>
                    </>}
                    {Object.values(r.tags).flat().slice(0,2).length > 0 && <>
                      <span style={{ opacity: 0.5 }}>·</span>
                      <span>{Object.values(r.tags).flat().slice(0,2).join(' · ')}</span>
                    </>}
                  </div>
                </div>
                {onPreview && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onPreview(r) }}
                    title="View details"
                    style={{
                      all: 'unset', cursor: 'pointer', padding: 6, borderRadius: 999,
                      color: theme.muted,
                    }}
                  >
                    <Ic.more width="16" height="16"/>
                  </button>
                )}
                <Ic.plus width="16" height="16" style={{ color: theme.accent }}/>
              </div>
            )}
          />
        </div>
        {currentRecipeId !== undefined && (
          <div style={{ borderTop: `1px solid ${theme.line}`, padding: '10px 18px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={onClear} style={{
              all: 'unset', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: theme.radius,
              fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
              textTransform: 'uppercase', color: theme.accentInk,
            }}>
              <Ic.trash width="14" height="14"/> Clear slot
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  )
}
